import { emptyRoles } from 'utils/constants';
import { AzurePermission, PortalUser, Session } from 'models';

enum PermissionAttributes {
  ADMIN = 'Admin',
  READONLY = 'Readonly',
}

/**
 * Validate role and handle restrictions
 */
export function validateAzureEditPermission(
  permissions: AzurePermission[],
): boolean {
  return permissions.some(({ permission }) => {
    // Restrict for readonly permissions
    if (permission.includes(PermissionAttributes.READONLY)) {
      return false;
    }

    // Restrict for non admin permissions
    if (!permission.includes(PermissionAttributes.ADMIN)) {
      return false;
    }
    return false;
  });
}

export function getUserRoles(session: Session): AzurePermission[] {
  return session.portalUser?.roles ?? emptyRoles;
}

export function hasAzureAccess(user: PortalUser): boolean {
  return user.roles.length > 0;
}

export function hasDeviceAccess(user: PortalUser): boolean {
  return user.roles.some(
    (permission) =>
      permission.permission === 'GammaMember' ||
      permission.permission === 'EmbeddedDeveloper' ||
      permission.permission === 'Tester' ||
      permission.permission === 'ReleaseManager' ||
      permission.permission === 'Management' ||
      permission.permission === 'FirstLineSupport',
  );
}

export function hasDeviceUpdateAccess(user: PortalUser): boolean {
  return user.roles.some(
    (permission) =>
      permission.permission === 'GammaMember' ||
      permission.permission === 'EmbeddedDeveloper' ||
      permission.permission === 'Tester',
  );
}

export function hasSetDesiredAccess(user: PortalUser): boolean {
  return user.roles.some(
    (permission) =>
      permission.permission === 'GammaMember' ||
      permission.permission === 'EmbeddedDeveloper' ||
      permission.permission === 'Tester',
  );
}

export function hasSoftwareReadAccess(user: PortalUser): boolean {
  return user.roles.some(
    (permission) =>
      permission.permission === 'GammaMember' ||
      permission.permission === 'EmbeddedDeveloper' ||
      permission.permission === 'Tester' ||
      permission.permission === 'ReleaseManager' ||
      permission.permission === 'Management' ||
      permission.permission === 'FirstLineSupport',
  );
}

export function hasSoftwareUploadAccess(user: PortalUser): boolean {
  return user.roles.some(
    (permission) =>
      permission.permission === 'GammaMember' ||
      permission.permission === 'EmbeddedDeveloper' ||
      permission.permission === 'Tester' ||
      permission.permission === 'ReleaseManager',
  );
}

export function hasRolloutReadAccess(user: PortalUser): boolean {
  return user.roles.some(
    (permission) =>
      permission.permission === 'GammaMember' ||
      permission.permission === 'EmbeddedDeveloper' ||
      permission.permission === 'Tester' ||
      permission.permission === 'ReleaseManager' ||
      permission.permission === 'Management' ||
      permission.permission === 'FirstLineSupport',
  );
}

export function hasRolloutCreateAccess(user: PortalUser): boolean {
  return user.roles.some(
    (permission) =>
      permission.permission === 'GammaMember' ||
      permission.permission === 'ReleaseManager',
  );
}

export function hasRolloutStartAccess(user: PortalUser): boolean {
  return user.roles.some(
    (permission) =>
      permission.permission === 'GammaMember' ||
      permission.permission === 'ReleaseApprover',
  );
}

export function hasAssignSoftwareAccess(user: PortalUser): boolean {
  return user.roles.some(
    (permission) =>
      permission.permission === 'GammaMember' ||
      permission.permission === 'EmbeddedDeveloper' ||
      permission.permission === 'Tester',
  );
}

export function hasHusqiotBusAccess(user: PortalUser): boolean {
  return user.roles.some(
    (permission) => permission.permission === 'GammaMember',
  );
}
export function hasLwm2mLogsAccess(user: PortalUser): boolean {
  return user.roles.some(
    (permission) =>
      permission.permission === 'GammaMember' ||
      permission.permission === 'EmbeddedDeveloper' ||
      permission.permission === 'Tester',
  );
}

export function hasAdminAccess(user: PortalUser): boolean {
  return user.roles.some(
    (permission) => permission.permission === 'GammaMember',
  );
}

export function addUserRoleToDataLayer(portalUser: PortalUser | null): void {
  if (portalUser) {
    const role = portalUser.roles?.find((r) => r.permission === 'GammaMember')
      ? 'gamma_member'
      : portalUser.roles?.length === 1 &&
          portalUser?.roles[0]?.permission === 'FirstLineSupport'
        ? 'first_line_support'
        : 'power_user';
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'userRole',
      userRole: role,
    });
  }
}
